import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Badge, Spinner } from 'react-bootstrap';
import { FaMapMarkerAlt, FaPlus, FaEdit, FaTrash, FaSearch, FaBuilding, FaWarehouse, FaGasPump, FaUser, FaEllipsisH, FaSync } from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { GetFromApi, PostToApi, PutToApi, DeleteFromApi } from '../utils/api';
import 'leaflet/dist/leaflet.css';
import '../styles/Pages.css';

// Fix for leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Landmarks = () => {
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedLandmark, setSelectedLandmark] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [landmarks, setLandmarks] = useState([]);
  const [formData, setFormData] = useState({ name: '', category: 'other', address: '', description: '', lat: 23.0225, lng: 72.5714 });

  const categories = [
    { id: 'office', name: 'Office', icon: FaBuilding, color: '#3B82F6' },
    { id: 'warehouse', name: 'Warehouse', icon: FaWarehouse, color: '#22C55E' },
    { id: 'gas-station', name: 'Gas Station', icon: FaGasPump, color: '#F59E0B' },
    { id: 'client', name: 'Client', icon: FaUser, color: '#A855F7' },
    { id: 'other', name: 'Other', icon: FaEllipsisH, color: '#6B7280' }
  ];

  const fetchLandmarks = async () => {
    try {
      setLoading(true);
      const endpoint = filterCategory === 'all' ? '/landmarks' : `/landmarks?category=${filterCategory}`;
      const response = await GetFromApi(endpoint);
      if (response.success) setLandmarks(response.data);
    } catch (error) {
      console.error('Error fetching landmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLandmarks(); }, [filterCategory]);

  const filteredLandmarks = landmarks.filter(landmark => 
    landmark.name?.toLowerCase().includes(searchTerm.toLowerCase()) || landmark.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryInfo = (catId) => categories.find(c => c.id === catId) || categories[4];

  const handleOpenModal = (landmark = null) => {
    if (landmark) {
      setEditMode(true);
      setFormData({ name: landmark.name, category: landmark.category, address: landmark.address, description: landmark.description, lat: landmark.lat, lng: landmark.lng });
      setSelectedLandmark(landmark);
    } else {
      setEditMode(false);
      setFormData({ name: '', category: 'other', address: '', description: '', lat: 23.0225, lng: 72.5714 });
      setSelectedLandmark(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => { setShowModal(false); setSelectedLandmark(null); setEditMode(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editMode && selectedLandmark) {
        await PutToApi(`/landmarks/${selectedLandmark.id}`, formData);
      } else {
        await PostToApi('/landmarks', formData);
      }
      await fetchLandmarks();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving landmark:', error);
      alert('Error saving landmark: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this landmark?')) {
      try {
        await DeleteFromApi(`/landmarks/${id}`);
        await fetchLandmarks();
      } catch (error) {
        console.error('Error deleting landmark:', error);
      }
    }
  };

  const mapCenter = [23.0225, 72.5714];

  const createCategoryIcon = (category) => {
    const catInfo = getCategoryInfo(category);
    return L.divIcon({
      className: 'custom-landmark-icon',
      html: `<div style="width: 32px; height: 32px; background-color: ${catInfo.color}; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); display: flex; align-items: center; justify-content: center;"><div style="transform: rotate(45deg); color: white; font-size: 14px;">📍</div></div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 32]
    });
  };

  return (
    <Container fluid className="p-3">
      <div className="page-header">
        <h4 className="page-title">Landmarks / POI</h4>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={fetchLandmarks} disabled={loading} style={{ borderRadius: '8px' }}>{loading ? <Spinner size="sm" /> : <FaSync />}</Button>
          <Button variant="primary" onClick={() => handleOpenModal()} style={{ borderRadius: '8px' }}><FaPlus className="me-2" /> Add Landmark</Button>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={2} sm={6} className="mb-3">
          <Card className="stats-card border-0 shadow-sm">
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' }}><FaMapMarkerAlt /></div>
              <div><div className="stats-value">{landmarks.length}</div><div className="stats-label">Total</div></div>
            </div>
          </Card>
        </Col>
        {categories.slice(0, 4).map(cat => {
          const count = landmarks.filter(l => l.category === cat.id).length;
          const IconComponent = cat.icon;
          return (
            <Col key={cat.id} md={2} sm={6} className="mb-3">
              <Card className={`stats-card border-0 shadow-sm ${filterCategory === cat.id ? 'border border-2 border-primary' : ''}`}
                style={{ cursor: 'pointer' }} onClick={() => setFilterCategory(filterCategory === cat.id ? 'all' : cat.id)}>
                <div className="d-flex align-items-center">
                  <div className="icon-box me-3" style={{ backgroundColor: `${cat.color}20`, color: cat.color }}><IconComponent /></div>
                  <div><div className="stats-value">{count}</div><div className="stats-label">{cat.name}</div></div>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Row>
        <Col lg={4} className="mb-4">
          <Card className="page-card h-100">
            <Card.Header className="bg-white border-bottom"><h6 className="mb-0 fw-semibold text-dark">Landmark List</h6></Card.Header>
            <Card.Body>
              <div className="search-input-wrapper mb-3">
                <FaSearch className="search-icon" />
                <Form.Control type="text" placeholder="Search landmarks..." value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)} className="form-control-custom" style={{ paddingLeft: '2.5rem' }} />
              </div>
              {loading ? (
                <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
              ) : (
                <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                  {filteredLandmarks.map(landmark => {
                    const catInfo = getCategoryInfo(landmark.category);
                    const IconComponent = catInfo.icon;
                    return (
                      <div key={landmark.id} className={`geofence-list-item ${selectedLandmark?.id === landmark.id ? 'active' : ''}`} onClick={() => setSelectedLandmark(landmark)}>
                        <div className="icon-box me-3" style={{ backgroundColor: `${catInfo.color}20`, color: catInfo.color, width: 40, height: 40 }}><IconComponent /></div>
                        <div className="flex-grow-1">
                          <div className="fw-semibold">{landmark.name}</div>
                          <small className="text-muted">{landmark.address?.substring(0, 40)}...</small>
                        </div>
                        <div className="ms-2">
                          <button className="action-btn edit me-1" onClick={(e) => { e.stopPropagation(); handleOpenModal(landmark); }}><FaEdit size={14} /></button>
                          <button className="action-btn delete" onClick={(e) => { e.stopPropagation(); handleDelete(landmark.id); }}><FaTrash size={14} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={8} className="mb-4">
          <Card className="page-card h-100">
            <Card.Header className="bg-white border-bottom"><h6 className="mb-0 fw-semibold text-dark">Location Map</h6></Card.Header>
            <Card.Body className="p-0">
              <div style={{ height: '500px' }}>
                <MapContainer center={mapCenter} zoom={12} style={{ height: '100%', width: '100%' }} zoomControl={true}>
                  <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {landmarks.map(landmark => (
                    <Marker key={landmark.id} position={[parseFloat(landmark.lat), parseFloat(landmark.lng)]} icon={createCategoryIcon(landmark.category)}>
                      <Popup><strong>{landmark.name}</strong><br /><small>{landmark.address}</small></Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={handleCloseModal} centered className="modal-custom">
        <Modal.Header closeButton><Modal.Title>{editMode ? 'Edit Landmark' : 'Add Landmark'}</Modal.Title></Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3"><Form.Label className="form-label-custom">Name</Form.Label><Form.Control type="text" className="form-control-custom" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required /></Form.Group>
            <Form.Group className="mb-3"><Form.Label className="form-label-custom">Category</Form.Label>
              <Form.Select className="form-control-custom" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3"><Form.Label className="form-label-custom">Address</Form.Label><Form.Control type="text" className="form-control-custom" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label className="form-label-custom">Description</Form.Label><Form.Control as="textarea" rows={2} className="form-control-custom" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} /></Form.Group>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label className="form-label-custom">Latitude</Form.Label><Form.Control type="number" step="0.000001" className="form-control-custom" value={formData.lat} onChange={(e) => setFormData({...formData, lat: parseFloat(e.target.value)})} required /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label className="form-label-custom">Longitude</Form.Label><Form.Control type="number" step="0.000001" className="form-control-custom" value={formData.lng} onChange={(e) => setFormData({...formData, lng: parseFloat(e.target.value)})} required /></Form.Group></Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={handleCloseModal}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>{saving ? <Spinner size="sm" className="me-2" /> : null}{editMode ? 'Save Changes' : 'Add Landmark'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Landmarks;
